import { useMemo } from 'react';
import { useIsHostedPage } from './useIsHostedPage';
import { PREFILL_QUERY_PARAMS } from '@charitystack/shared';

/**
 * Whitelisted URL query param keys that can pre-fill donation form fields.
 * Only these keys are ever read from the URL.
 *
 * CON-154: the list lives in `@charitystack/shared` (`urlPrivacy.js`) so the
 * analytics scrubber that masks these values in PostHog's `$current_url` reads
 * the SAME list — the allowlist and the mask cannot drift apart. The documented
 * meaning of each key is beside the constant there.
 */
const ALLOWED_PARAMS = PREFILL_QUERY_PARAMS;

/**
 * Merchant-friendly frequency aliases → internal enum.
 * For embedded forms, this normalization happens in the header script (url-params.js).
 * For hosted pages, the hook must normalize directly since there's no header script layer.
 */
const FREQUENCY_ALIASES = {
  'one-time': 'ONE_TIME',
  onetime: 'ONE_TIME',
  one_time: 'ONE_TIME',
  once: 'ONE_TIME',
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  yearly: 'ANNUALLY',
  annually: 'ANNUALLY',
  annual: 'ANNUALLY',
};

/**
 * Hook that reads whitelisted query params from the URL to pre-fill form fields.
 *
 * Works in two contexts:
 * 1. Hosted page (URLs like /donate/{formID}): reads params directly (e.g., ?amount=50)
 *    Frequency values are normalized here (e.g., "monthly" → "MONTHLY", "once" → "ONE_TIME")
 * 2. Embedded form (iframe): reads cs_* prefixed params (e.g., ?cs_amount=50)
 *    These cs_* params are forwarded from the parent page URL by the header script
 *    (url-params.js → insert-elements.js) to bridge the cross-origin iframe barrier.
 *    Frequency is already normalized by the header script before reaching the iframe.
 *
 * Returns an empty object when no matching params exist.
 *
 * Usage:
 *   const urlOverrides = useUrlPrefill();
 *   // urlOverrides = { amount: '50', fund: 'Zakat', frequency: 'MONTHLY' }
 *   // or {} if no matching params
 *
 * @returns {Object} An object with whitelisted param key-value pairs (lowercase keys)
 */
export const useUrlPrefill = () => {
  const isHostedPage = useIsHostedPage();

  return useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const overrides = {};

    if (isHostedPage) {
      // Hosted page: read params directly (e.g., ?amount=50&fund=Zakat)
      for (const [key, value] of urlParams.entries()) {
        const keyLower = key.toLowerCase();
        if (value.trim() === '') continue;

        // Allow ci_* and gci_* (custom input) params through without whitelisting
        if (keyLower.startsWith('ci_') || keyLower.startsWith('gci_')) {
          overrides[keyLower] = value.trim();
          continue;
        }

        // Allow ticket and seat*_ params through for event forms
        if (keyLower === 'ticket' || keyLower === 'ticketseats' || keyLower.match(/^seat\d+_/)) {
          overrides[keyLower] = value.trim();
          continue;
        }

        if (ALLOWED_PARAMS.includes(keyLower)) {
          let normalizedValue = value.trim();

          // Normalize frequency aliases to internal enum (e.g., "monthly" → "MONTHLY")
          if (keyLower === 'frequency') {
            normalizedValue =
              FREQUENCY_ALIASES[normalizedValue.toLowerCase()] || normalizedValue.toUpperCase();
          }

          overrides[keyLower] = normalizedValue;
        }
      }
    } else {
      // Embedded form (iframe): read cs_* prefixed params (e.g., ?cs_amount=50)
      // The header script (url-params.js) reads whitelisted params from the parent page URL,
      // normalizes them, and appends them as cs_*-prefixed params on the iframe src.
      for (const [key, value] of urlParams.entries()) {
        const keyLower = key.toLowerCase();
        if (keyLower.startsWith('cs_')) {
          const paramName = keyLower.slice(3); // Remove 'cs_' prefix
          if (value.trim() === '') continue;

          // Allow ci_* and gci_* (custom input) params through without whitelisting
          if (paramName.startsWith('ci_') || paramName.startsWith('gci_')) {
            overrides[paramName] = value.trim();
            continue;
          }

          // Allow ticket and seat*_ params through for event forms
          if (
            paramName === 'ticket' ||
            paramName === 'ticketseats' ||
            paramName.match(/^seat\d+_/)
          ) {
            overrides[paramName] = value.trim();
            continue;
          }

          if (ALLOWED_PARAMS.includes(paramName)) {
            overrides[paramName] = value.trim();
          }
        }
      }
    }

    return overrides;
  }, [isHostedPage]);
};
